// Utility helpers shared across message attachment components.

const MARKDOWN_CONTENT_TYPES = new Set(['text/markdown', 'text/x-markdown']);
const MARKDOWN_FALLBACK_CONTENT_TYPES = new Set(['', 'application/octet-stream', 'text/plain']);
const MARKDOWN_FILE_EXTENSION = /\.(?:md|markdown|mdown|mkd|mkdn)$/i;

function baseContentType(contentType) {
  return typeof contentType === 'string' ? contentType.split(';', 1)[0].trim().toLowerCase() : '';
}

export function isImageContentType(contentType) {
  return baseContentType(contentType).startsWith('image/');
}

export function isMarkdownContentType(contentType) {
  return MARKDOWN_CONTENT_TYPES.has(baseContentType(contentType));
}

export function isMarkdownFileName(fileName) {
  return typeof fileName === 'string' && MARKDOWN_FILE_EXTENSION.test(fileName.trim());
}

export function isMarkdownAttachment(attachment) {
  const contentType = attachment?.metadata?.content_type;
  if (isMarkdownContentType(contentType)) return true;

  const fileName = attachment?.metadata?.file_name || attachment?.fileName;
  return MARKDOWN_FALLBACK_CONTENT_TYPES.has(baseContentType(contentType)) && isMarkdownFileName(fileName);
}

export function resolveUploadContentType(file) {
  const rawContentType = typeof file?.type === 'string' ? file.type.trim() : '';
  const contentType = baseContentType(rawContentType);
  if (isMarkdownContentType(contentType)) return 'text/markdown';
  if (isMarkdownFileName(file?.name) && ['', 'application/octet-stream', 'text/plain'].includes(contentType)) {
    return 'text/markdown';
  }

  return rawContentType || null;
}

export function safeDimension(value, fallback = 1) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }

  return Math.round(parsed);
}

export function parseThumbsize(thumbsize) {
  if (typeof thumbsize !== 'string') return null;

  const match = thumbsize.trim().match(/^(\d+)x(\d+)$/i);
  if (!match) return null;

  const width = safeDimension(match[1], null);
  const height = safeDimension(match[2], null);
  if (!width || !height) return null;

  return { width, height };
}

export function makePlaceholderDataUri(width, height) {
  const w = Math.max(1, Math.round(width || 1));
  const h = Math.max(1, Math.round(height || 1));
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><rect width="${w}" height="${h}" fill="#f3f4f6"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
